import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { finalize } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PosmScheduleCompletionReport } from '../models/posm-execution.models';
import { PosmService } from '../posm.service';
import { PendingActionTracker } from '../../shared/state/pending-action-tracker';

/** Historical schedule-level PDFs remain downloadable but new reports are issued at execution level. */
@Component({
    selector: 'app-posm-schedule-completion-report',
    standalone: true,
    imports: [CommonModule, ButtonModule, TagModule, PageDataPanelComponent],
    templateUrl: './posm-schedule-completion-report.component.html',
    styleUrls: ['./posm-schedule-completion-report.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmScheduleCompletionReportComponent implements OnChanges {
    @Input() projectId?: number | null;
    @Input() scheduleId?: number | null;
    @Input() owningCompanyId?: number | null;

    private readonly posmService = inject(PosmService);

    readonly reports = signal<PosmScheduleCompletionReport[]>([]);
    readonly loading = signal(false);
    readonly reportActions = new PendingActionTracker<string>();
    readonly error = signal<string | null>(null);

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['projectId'] || changes['scheduleId'] || changes['owningCompanyId']) && this.validContext()) {
            this.load();
        }
    }

    load(): void {
        if (!this.validContext()) return;
        this.loading.set(true);
        this.error.set(null);
        this.posmService.getScheduleCompletionReports(this.projectId!, this.scheduleId!, this.owningCompanyId!)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: reports => this.reports.set([...(reports || [])].sort((left, right) => (right.version || 0) - (left.version || 0))),
                error: error => {
                    this.reports.set([]);
                    this.error.set(error?.error?.message || 'Legacy completion reports could not be loaded.');
                }
            });
    }

    download(report: PosmScheduleCompletionReport): void {
        if (!this.validContext() || !report.id) return;
        const actionKey = this.downloadActionKey(report);
        if (!this.reportActions.begin(actionKey)) return;
        this.error.set(null);
        this.posmService.downloadScheduleCompletionReport(this.projectId!, this.scheduleId!, report.id, this.owningCompanyId!)
            .pipe(finalize(() => this.reportActions.end(actionKey)))
            .subscribe({
                next: blob => this.downloadBlob(blob, report.fileName || `installation-completion-v${report.version || 1}.pdf`),
                error: error => this.error.set(error?.error?.message || 'The legacy completion report could not be downloaded.')
            });
    }

    generatedBy(report: PosmScheduleCompletionReport): string {
        return report.generatedByActor?.name || (report.generatedByCustomerId ? `User #${report.generatedByCustomerId}` : '-');
    }

    downloadActionKey(report: PosmScheduleCompletionReport): string {
        return `download:${report.id}`;
    }

    private validContext(): boolean {
        return !!this.projectId && !!this.scheduleId && !!this.owningCompanyId;
    }

    private downloadBlob(blob: Blob, fileName: string): void {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    }
}
